import React, { useEffect, useImperativeHandle, forwardRef } from "react";
import { Box, Grid2 } from "@mui/material";
import { useForm, SubmitHandler } from "react-hook-form";
import User from "../../types/user";
import { useSelector } from "../../store";
import { useSkeletonLoader } from "../../hooks/useSkeletonLoader";
import FormTextField from "../forms/FormTextField";
import FormAutocomplete from "../forms/FormAutocomplete";
import checkRole, { UserRoles } from "../../helpers/checkUserRoles";
import Option from "../../types/option";
import getAdminAppRole from "../../helpers/getAdminAppRole";

interface FormProps {
  user?: User;
  type: "add" | "unauth" | "profile";
  onSubmit: SubmitHandler<User>;
}

export type UserFormSubmitHandler = () => void;

const AddEditUser = forwardRef<UserFormSubmitHandler, FormProps>(
  (props, ref) => {
    const { user, onSubmit, type } = props;

    const { userRoles, status } = useSelector((store) => store.user);
    const { dbUser } = useSelector((store) => store.auth);
    const { ssoStatus, provider } = useSelector((store) => store.provider);

    const { handleSubmit, control, watch, reset, setValue } = useForm<User>({
      mode: "onChange",
      defaultValues: type === "profile" ? dbUser : user,
    });

    const formValues = watch();

    useImperativeHandle(ref, () => handleSubmit(onSubmit));
    const { loadWithSkeleton } = useSkeletonLoader(status === "loading");

    useEffect(() => {
      if (!!user) {
        reset(user);
      }
    }, [reset, user]);

    const editableRoles = userRoles?.filter((role) => {
      const requiredAdminRole = getAdminAppRole(role.id as UserRoles);

      return (
        (requiredAdminRole && checkRole(dbUser!, requiredAdminRole)) ||
        checkRole(dbUser!, UserRoles.GlobalAdmin)
      );
    });

    const lockedRoles = (formValues.roles || []).filter((role) => {
      if (checkRole(dbUser!, UserRoles.GlobalAdmin)) return false;

      return !editableRoles?.some((r) => r.id === role.id);
    });

    return (
      <Box display="flex" flexDirection="row" width="100%">
        <Box display="flex" flexDirection="column" width="100%">
          <form>
            <Grid2 container spacing={2} paddingBottom={2}>
              <Grid2 size={{ xs: 12, sm: 6 }}>
                {loadWithSkeleton(
                  <FormTextField
                    label="First Name"
                    control={control}
                    fieldName="firstName"
                    required
                  />
                )}
              </Grid2>
              <Grid2 size={{ xs: 12, sm: 6 }}>
                {loadWithSkeleton(
                  <FormTextField
                    control={control}
                    fieldName="lastName"
                    label="Last Name"
                    required
                  />
                )}
              </Grid2>
              <Grid2 size={{ xs: 12, sm: 6 }}>
                {loadWithSkeleton(
                  <FormTextField
                    control={control}
                    fieldName="email"
                    label="Contact Email"
                    required
                    type="email"
                    disabled={ssoStatus?.useSso && type === "profile"}
                  />
                )}
              </Grid2>
              <Grid2 size={{ xs: 12, sm: 6 }}>
                {loadWithSkeleton(
                  <FormAutocomplete
                    control={control}
                    fieldName="roles"
                    options={userRoles || []}
                    defaultValue={formValues.roles}
                    required={type !== "profile"}
                    multiple
                    label="User Role"
                    onDisableOption={(option) => {
                      const isInEditableRoles = editableRoles?.some(
                        (r) => r.id === option.id
                      );
                      return !isInEditableRoles;
                    }}
                    onChange={(value) => {
                      setValue("roles", [
                        ...lockedRoles,
                        ...value?.filter(
                          (x: Option) => !lockedRoles.some((r) => r.id === x.id)
                        ),
                      ]);
                    }}
                    disabled={
                      type === "profile" ||
                      provider?.mappedRoles?.length! > 0 ||
                      (user?.roles &&
                        checkRole(user, UserRoles.GlobalAdmin) &&
                        !checkRole(dbUser!, UserRoles.GlobalAdmin)) ||
                      (user?.id === dbUser?.id &&
                        !checkRole(dbUser!, UserRoles.GlobalAdmin))
                    }
                  />
                )}
              </Grid2>
            </Grid2>
          </form>
        </Box>
      </Box>
    );
  }
);

export default AddEditUser;
